import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, Users, Sparkles, Download, Loader2, FolderOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getUserProfile, generateNatalReport, generateCompatibilityReport, generateDashaReport, listKarmicReports } from "@/lib/api";
import { ReportDetailsForm } from "@/components/ReportDetailsForm";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { motion } from "framer-motion";

const REPORT_META = {
  natal_report: { label: "Natal Report", icon: FileText, generate: generateNatalReport, filename: "natal_report.pdf" },
  dasha_forecast: { label: "Dasha Forecast", icon: Sparkles, generate: generateDashaReport, filename: "dasha_forecast.pdf" },
  compatibility: { label: "Compatibility Report", icon: Users, generate: generateCompatibilityReport, filename: "compatibility_report.pdf" },
  compatibility_report: { label: "Compatibility Report", icon: Users, generate: generateCompatibilityReport, filename: "compatibility_report.pdf" },
};

// Blob responses hide the JSON error body; parse it back out
async function readErrorDetail(e) {
  let data = e?.response?.data;
  if (data instanceof Blob) {
    try { data = JSON.parse(await data.text()); } catch { return null; }
  }
  return data?.detail ?? null;
}

function ReportRow({ tx, index }) {
  const meta = REPORT_META[tx.package_id];
  const Icon = meta.icon;
  const [busy, setBusy] = useState(false);
  const [needsDetails, setNeedsDetails] = useState(false);

  const download = async (details = {}) => {
    if (!tx.session_id) {
      toast.error("This purchase has no session reference and can't be re-downloaded.");
      return;
    }
    setBusy(true);
    try {
      const blob = await meta.generate({ session_id: tx.session_id, ...details });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = meta.filename;
      a.click();
      URL.revokeObjectURL(url);
      setNeedsDetails(false);
      toast.success("Report downloaded!");
    } catch (e) {
      const detail = await readErrorDetail(e);
      if (detail?.requires_birth_details) {
        setNeedsDetails(true);
        toast.info("Please enter the birth details for this report");
      } else {
        toast.error(detail?.message || (typeof detail === "string" ? detail : null) || "Could not generate the report. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-lg p-4"
      style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
      data-testid={`myreport-${index}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0">
            <Icon className="w-4 h-4 text-white/40" />
          </div>
          <div className="min-w-0">
            <p className="text-white/80 text-sm truncate" data-testid={`myreport-name-${index}`}>{tx.package_name || meta.label}</p>
            <p className="text-white/30 text-xs">
              {tx.created_at ? new Date(tx.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
            </p>
          </div>
        </div>
        <Button
          onClick={() => download()}
          disabled={busy}
          className="bg-white text-black hover:bg-white/90 rounded-full h-9 px-4 text-xs flex-shrink-0"
          data-testid={`myreport-download-${index}`}
        >
          {busy ? (
            <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Generating…</>
          ) : (
            <><Download className="w-3.5 h-3.5 mr-1.5" /> Download PDF</>
          )}
        </Button>
      </div>
      {needsDetails && (
        <div className="mt-4">
          <ReportDetailsForm
            packageId={tx.package_id}
            label={meta.label}
            initial={tx.metadata || {}}
            onSubmit={download}
            submitting={busy}
          />
        </div>
      )}
    </div>
  );
}

export default function MyReports() {
  const { user, loading: authLoading, loginWithGoogle } = useAuth();
  const [reports, setReports] = useState([]);
  const [karmic, setKarmic] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setLoading(false); return; }
    Promise.all([
      getUserProfile().then((p) => {
        const paid = (p?.purchases || []).filter(
          (tx) => tx.payment_status === "paid" && REPORT_META[tx.package_id]
        );
        setReports(paid);
      }).catch(() => {}),
      listKarmicReports().then((d) => setKarmic(d.reports || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [user, authLoading]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <img src="/logo.png" alt="Daily Nakshatra" className="h-12 w-auto logo-pulse" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center px-6">
          <img src="/logo.png" alt="Daily Nakshatra" className="h-12 w-auto mx-auto mb-4 opacity-40" />
          <h1 className="text-3xl font-light text-white/90 mb-3">Sign in to view your reports</h1>
          <p className="text-white/40 text-sm mb-6">Re-download any report you've purchased, anytime.</p>
          <Button onClick={loginWithGoogle} className="bg-white text-black hover:bg-white/90 rounded-full px-6 h-11 text-sm" data-testid="myreports-login-btn">
            Sign in with Google
          </Button>
          <div className="mt-4">
            <Link to="/" className="text-white/30 text-sm hover:text-white/50">Back to Dashboard</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center">
          <Link to="/profile" className="flex items-center gap-2 text-white/60 hover:text-white/90 transition-colors" data-testid="myreports-back-link">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm tracking-wider uppercase">Profile</span>
          </Link>
        </div>
      </nav>

      <main className="pt-14 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-light text-white/95 mb-2" data-testid="myreports-title">My Reports</h1>
            <p className="text-white/40 text-sm">Every report you've purchased — download the PDF again whenever you need it, free of charge.</p>
          </div>

          {karmic.length > 0 && (
            <div className="mb-6" data-testid="myreports-karmic-list">
              <p className="text-white/50 text-xs tracking-[0.15em] uppercase mb-3">Karmic Timelines</p>
              <div className="space-y-3">
                {karmic.map((k) => (
                  <button
                    key={k.id}
                    onClick={() => navigate(`/karmic?report=${k.id}`)}
                    className="w-full text-left rounded-lg p-4 flex items-center justify-between hover:bg-white/[0.04] transition-colors"
                    style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
                    data-testid={`myreport-karmic-${k.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-violet-500/10 flex items-center justify-center flex-shrink-0">
                        <Sparkles className="w-4 h-4 text-violet-300/70" />
                      </div>
                      <div>
                        <p className="text-white/80 text-sm">{k.name} · 18-month outlook</p>
                        <p className="text-white/30 text-xs">{k.generated_at ? new Date(k.generated_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"} · free to reopen</p>
                      </div>
                    </div>
                    <span className="text-white/40 text-xs">Open →</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {reports.length > 0 ? (
            <div className="space-y-3" data-testid="myreports-list">
              {reports.map((tx, i) => (
                <ReportRow key={tx.session_id || tx.id || i} tx={tx} index={i} />
              ))}
            </div>
          ) : karmic.length === 0 ? (
            <div
              className="rounded-lg p-10 text-center"
              style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
              data-testid="myreports-empty"
            >
              <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                <FolderOpen className="w-6 h-6 text-white/30" />
              </div>
              <p className="text-white/50 text-sm mb-1">{karmic.length > 0 ? "No purchased PDF reports yet" : "No reports yet"}</p>
              <p className="text-white/30 text-xs mb-5">Purchase a Natal, Compatibility or Dasha report to see it here.</p>
              <Button
                onClick={() => navigate("/pricing")}
                className="bg-white/10 text-white/70 hover:bg-white/15 border border-white/10 rounded-full px-5 h-9 text-xs"
                data-testid="myreports-browse-btn"
              >
                View Reports & Pricing
              </Button>
            </div>
          ) : null}
        </motion.div>
      </main>
    </div>
  );
}
